import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  LuPenLine,
  LuRefreshCw,
  LuChevronDown,
  LuArrowLeft,
  LuTrash2,
  LuDownload,
  LuPlus,
  LuImage,
  LuX,
  LuCheck,
  LuZoomIn,
} from "react-icons/lu";
import i18n from "i18next";
import { getToken } from "../../../../common/environment_config.es6";
import { approveJob } from "../../../../common/server_utils.es6";
import { onJobsSettled, getSettledOutcome, SETTLED_APPROVED } from "../../../utils/jobSettled";
import { credentialsFor } from "../../../../common/utils";
import { isFileTooLarge, fileTooLargeMessage } from "../../../../common/upload_limits.es6";

const STATUS_COLOR = {
  pending: "#f59e0b",
  generating: "#17a2b8",
  processing: "#17a2b8",
  completed: "#22c55e",
  failed: "#ef4444",
  error: "#ef4444",
};

const STATUS_BG = {
  pending: "#fffbeb",
  generating: "#ecfeff",
  processing: "#ecfeff",
  completed: "#f0fdf4",
  failed: "#fef2f2",
  error: "#fef2f2",
};

// Providers that can also generate the still preview image. Video-only models
// (veo, veo-fast, seedance, ltx) can't, so they fall back to gemini.
const IMAGE_CAPABLE_PROVIDERS = ["grok"];
const resolvePreviewImageProvider = (videoProvider) =>
  IMAGE_CAPABLE_PROVIDERS.includes(videoProvider) ? videoProvider : "gemini";

const VideoStoryboardPanel = ({
  isCreating,
  scenes,
  sceneJobs,
  editingScene,
  editPromptValue,
  onEditStart,
  onEditSave,
  isSavingScene,
  onEditCancel,
  onEditChange,
  onRegenerate,
  onCancelJob,
  onGenerateNext,
  onBack,
  onClear,
  onAddToScene,
  onAddScene,
  uploadFunction,
  sceneImages = [],
  aspectRatio,
  provider,
  apiUrl,
  storyboardId,
  onUpdateScene,
  onStoryboardNotFound,
  onInsufficientBalance,
}) => {
  // Sahne klibi onaylandıysa (tepsiden: JobWidgetHost videoyu sahneye zaten
  // ekledi; ya da buradan) "Sahneye Ekle" sessizce ikinci kopya eklerdi → durumu göster, buton
  // "Tekrar ekle" olsun. Kapanış defteri kalıcı; sinyal gelince yeniden çiz.
  const [, setSettledTick] = useState(0);
  useEffect(() => onJobsSettled(() => setSettledTick((t) => t + 1)), []);
  const addedFromTray = (jobId) => getSettledOutcome(jobId) === SETTLED_APPROVED;
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [expandedVideos, setExpandedVideos] = useState({});
  const [previewLoading, setPreviewLoading] = useState({});
  // Job bittiği anda lokal olarak işaretlenir. scene.previewImageUrl prop'u parent'tan
  // bir render sonra geldiği için, arada butonun "Üret"e düşüp geri "Tekrar Üret"e
  // dönmesini (flash) engeller.
  const [previewDone, setPreviewDone] = useState({});
  const [expandedPreviews, setExpandedPreviews] = useState({});
  const [expandedSceneImages, setExpandedSceneImages] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lastCompletedSceneIndex, setLastCompletedSceneIndex] = useState(-1);
  const [editErrors, setEditErrors] = useState({});
  const [cancellingJobs, setCancellingJobs] = useState({});

  // İlk sahnenin başlangıç görseli (image-to-video). Upload sonrası URL burada tutulur.
  // regenerate/next bu URL'i referenceImageUrl olarak gönderir.
  const [sceneRefImages, setSceneRefImages] = useState({});
  const [uploadingRef, setUploadingRef] = useState({});

  const refUrlToSend = (idx) => sceneRefImages[idx]?.url;

  const handleRefImageSelect = (idx, file) => {
    if (!file || !uploadFunction) return;
    // 500 MB üst sınır guard'ı — aşan dosyayı kabul etme.
    if (isFileTooLarge(file)) {
      alert(fileTooLargeMessage());
      return;
    }
    if (!file.type?.startsWith("image/")) return;
    setUploadingRef((prev) => ({ ...prev, [idx]: true }));
    uploadFunction(
      file,
      "",
      (url) => {
        setUploadingRef((prev) => ({ ...prev, [idx]: false }));
        if (url) {
          setSceneRefImages((prev) => ({ ...prev, [idx]: { url, name: file.name } }));
        }
      },
      () => {}
    );
  };

  const handleRefImageClear = (idx) => {
    setSceneRefImages((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const renderRefImageControl = (idx) => {
    if (!uploadFunction) return null;
    const ref = sceneRefImages[idx];
    const isUploading = uploadingRef[idx];
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
          flexWrap: "wrap",
        }}
      >
        {ref ? (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img
              src={ref.url}
              alt="reference"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "4px",
                border: "1px solid #e2e8f0",
              }}
            />
            <span
              style={{ fontSize: "11px", color: "#475569", maxWidth: "160px" }}
              title={ref.name}
            >
              {i18n.t("FileModal.APIProviders.VideoGeneration.StartingImageAsReference")}
            </span>
            <button
              type="button"
              onClick={() => handleRefImageClear(idx)}
              className="add-video-modal__btn add-video-modal__btn--ghost"
              style={{
                fontSize: 11,
                padding: "2px 6px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                color: "#ef4444",
              }}
            >
              <LuX size={11} />
              {i18n.t("FileModal.APIProviders.VideoGeneration.ReferenceImageRemove")}
            </button>
          </div>
        ) : (
          <label
            className="add-video-modal__btn add-video-modal__btn--ghost"
            style={{
              fontSize: 11,
              padding: "4px 10px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: isUploading ? "default" : "pointer",
              opacity: isUploading ? 0.7 : 1,
            }}
          >
            {isUploading ? (
              <span className="add-video-modal__scene-spinner" />
            ) : (
              <LuImage size={12} />
            )}
            {isUploading
              ? i18n.t("FileModal.APIProviders.VideoGeneration.ReferenceImageUploading")
              : i18n.t("FileModal.APIProviders.VideoGeneration.StartingImageAsReference")}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                handleRefImageSelect(idx, file);
              }}
            />
          </label>
        )}
      </div>
    );
  };

  // Yeni sahne ekleme formu. Yeni API: sadece videoPrompt (zorunlu, min 20) +
  // afterScene ("" = sona ekle). Narration videoPrompt'tan türetilir, süreyi provider
  // belirler — bu yüzden ayrı narration/duration alanı yok.
  const [showAddForm, setShowAddForm] = useState(false);
  const [addPrompt, setAddPrompt] = useState("");
  const [addAfterScene, setAddAfterScene] = useState(""); // "" = sona ekle
  const [isAddingScene, setIsAddingScene] = useState(false);
  const [addError, setAddError] = useState(null);

  const resetAddForm = () => {
    setShowAddForm(false);
    setAddPrompt("");
    setAddAfterScene("");
    setAddError(null);
  };

  const handleAddSceneSubmit = async () => {
    setAddError(null);
    if (addPrompt.trim().length < 20) {
      setAddError(i18n.t("FileModal.APIProviders.VideoGeneration.AddScenePromptTooShort"));
      return;
    }
    setIsAddingScene(true);
    try {
      await onAddScene?.({
        videoPrompt: addPrompt.trim(),
        afterScene: addAfterScene,
      });
      resetAddForm();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("FileModal.APIProviders.VideoGeneration.EditSaveError");
      setAddError(errorMessage);
    } finally {
      setIsAddingScene(false);
    }
  };

  const handleCancelClick = async (idx) => {
    if (cancellingJobs[idx]) return;
    setCancellingJobs((prev) => ({ ...prev, [idx]: true }));
    try {
      await onCancelJob?.(idx);
    } finally {
      setCancellingJobs((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
    }
  };

  const togglePrompt = (idx) => {
    setExpandedPrompts((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleVideo = (idx) => {
    setExpandedVideos((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const togglePreview = (idx) => {
    setExpandedPreviews((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleSceneImages = (idx) => {
    setExpandedSceneImages((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSelectSceneImage = (idx, url) => {
    setSceneRefImages((prev) => {
      // Aynı görsele tekrar tıklanırsa seçimi kaldır (toggle).
      if (prev[idx]?.url === url) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: { url, name: "scene-image" } };
    });
  };

  // Seçilebilir görsel thumbnail'i (sahne görselleri + önizleme görselleri ortak).
  // Tıkla → referans olarak seç/kaldır; sağ-alt fullscreen; seçiliyse tik.
  const renderSelectableImageThumb = (idx, url, key, selectable = true) => {
    const isSelected = selectable && sceneRefImages[idx]?.url === url;
    return (
      <div
        key={key}
        role={selectable ? "button" : undefined}
        tabIndex={selectable ? 0 : undefined}
        onClick={selectable ? () => handleSelectSceneImage(idx, url) : undefined}
        title={
          selectable
            ? isSelected
              ? i18n.t("FileModal.APIProviders.VideoGeneration.ReferenceImageSet")
              : i18n.t("FileModal.APIProviders.VideoGeneration.SceneImageSelect")
            : undefined
        }
        style={{
          position: "relative",
          padding: 0,
          aspectRatio: "1/1",
          borderRadius: "6px",
          overflow: "hidden",
          cursor: selectable ? "pointer" : "default",
          background: "#f8fafc",
          border: isSelected ? "2px solid #a963f5" : "1px solid #e2e8f0",
          boxShadow: isSelected ? "0 0 0 3px rgba(169,99,245,0.15)" : "none",
        }}
      >
        <img
          src={url}
          alt="scene"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
        {/* View fullscreen (Aidea Wizard ile aynı) */}
        <button
          type="button"
          title={i18n.t("View fullscreen")}
          onClick={(e) => {
            e.stopPropagation();
            setLightboxUrl(url);
          }}
          style={{
            position: "absolute",
            bottom: 4,
            right: 4,
            background: "rgba(0,0,0,0.55)",
            border: "none",
            borderRadius: "50%",
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            cursor: "pointer",
            backdropFilter: "blur(4px)",
            zIndex: 2,
          }}
        >
          <LuZoomIn size={12} />
        </button>
        {isSelected && (
          <span
            style={{
              position: "absolute",
              top: "2px",
              right: "2px",
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              background: "#a963f5",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LuCheck size={12} />
          </span>
        )}
      </div>
    );
  };

  const selectableGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
    gap: "8px",
  };

  // Auto-expand only when a NEW scene completes
  useEffect(() => {
    if (!scenes || scenes.length === 0) return;

    // Find the last completed scene
    let lastCompletedIdx = -1;
    for (let i = scenes.length - 1; i >= 0; i--) {
      const idx = scenes[i].index;
      const job = sceneJobs[idx] || {};
      const status = job.status || scenes[i].status || "pending";
      if (status === "completed" && scenes[i].videoUrl) {
        lastCompletedIdx = idx;
        break;
      }
    }

    // Only expand if this is a NEW completion (different from last tracked)
    if (lastCompletedIdx !== -1 && lastCompletedIdx !== lastCompletedSceneIndex) {
      setLastCompletedSceneIndex(lastCompletedIdx);
      setExpandedVideos((prev) => ({
        ...prev,
        [lastCompletedIdx]: true,
      }));
    }
  }, [scenes, sceneJobs, lastCompletedSceneIndex]);

  // Modern input/textarea style
  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    fontSize: "13px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    background: "#fff",
    color: "#1f2937",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    lineHeight: "1.5",
    resize: "vertical",
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "#3b82f6";
    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  const handleEditSave = async (idx) => {
    setEditErrors((prev) => ({ ...prev, [idx]: null }));
    try {
      await onEditSave(idx);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        i18n.t("FileModal.APIProviders.VideoGeneration.EditSaveError");
      setEditErrors((prev) => ({ ...prev, [idx]: errorMessage }));
    }
  };

  const handleEditStart = (idx, prompt) => {
    setEditErrors((prev) => ({ ...prev, [idx]: null }));
    // Prompt akordeonu kapalıyken de Edit'e basılabilir; textarea kapalı akordeonun
    // içinde olduğu için düzenlemeye geçerken bölümü açık zorla.
    setExpandedPrompts((prev) => ({ ...prev, [idx]: true }));
    onEditStart(idx, prompt);
  };

  const handleEditCancel = () => {
    if (editingScene !== null) {
      setEditErrors((prev) => ({ ...prev, [editingScene]: null }));
    }
    onEditCancel();
  };

  const handleDownload = async (videoUrl, filename) => {
    try {
      const response = await fetch(videoUrl, credentialsFor(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // Preview image artık async: POST 202 { jobId } döner → GET api/jobs/:id ile
  // job "inspect" (completed) olana kadar poll edilir, resultUrl görsel URL'idir.
  const previewPollRefs = useRef({});
  const PREVIEW_POLL_MS = 3000;
  const PREVIEW_MAX_ATTEMPTS = 60;

  useEffect(
    () => () => {
      Object.values(previewPollRefs.current).forEach((t) => clearTimeout(t));
    },
    []
  );

  const finishPreview = (idx, imageUrl, imageUrls) => {
    if (imageUrl) {
      const updates = { previewImageUrl: imageUrl };
      // previewImageUrls = o sahne için üretilmiş tüm önizleme görselleri (liste).
      if (Array.isArray(imageUrls) && imageUrls.length) updates.previewImageUrls = imageUrls;
      onUpdateScene?.(idx, updates);
      setExpandedPreviews((prev) => ({ ...prev, [idx]: true }));
      setPreviewDone((prev) => ({ ...prev, [idx]: true }));
    }
    setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
  };

  const extractPreviewUrl = (job) =>
    job?.resultUrl ||
    job?.metadata?.previewImageUrl ||
    job?.metadata?.scene?.previewImageUrl ||
    job?.metadata?.generatedImages?.[0]?.url ||
    job?.result?.images?.[0]?.url ||
    job?.result?.url ||
    null;

  const extractPreviewUrls = (job) => {
    const list =
      job?.metadata?.scene?.previewImageUrls ||
      job?.metadata?.previewImageUrls ||
      job?.result?.scene?.previewImageUrls ||
      null;
    return Array.isArray(list) ? list.filter(Boolean) : [];
  };

  const pollPreviewJob = (idx, jobId, attempt = 0) => {
    if (attempt > PREVIEW_MAX_ATTEMPTS) {
      setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
      return;
    }
    previewPollRefs.current[idx] = setTimeout(async () => {
      try {
        const res = await fetch(`${apiUrl}api/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const job = await res.json();
        const status = job?.status;
        // Worker bitince status "inspect" (completed); metadata.status "completed".
        const isDone =
          status === "inspect" || status === "completed" || job?.metadata?.status === "completed";
        if (isDone) {
          const previewUrl = extractPreviewUrl(job);
          finishPreview(idx, previewUrl, extractPreviewUrls(job));
          // Önizleme sahne kartına otomatik uygulandı → widget kartı Geçmiş'e geçsin.
          if (previewUrl) approveJob(jobId);
        } else if (status === "failed" || job?.metadata?.status === "failed") {
          setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
        } else {
          pollPreviewJob(idx, jobId, attempt + 1);
        }
      } catch (err) {
        console.error("[VideoStoryboardPanel] Preview poll error:", err);
        pollPreviewJob(idx, jobId, attempt + 1);
      }
    }, PREVIEW_POLL_MS);
  };

  const handlePreviewImage = async (idx, prompt) => {
    if (previewLoading[idx] || !storyboardId) return;
    setPreviewLoading((prev) => ({ ...prev, [idx]: true }));
    try {
      const res = await fetch(
        `${apiUrl}api/course-video/storyboard/${storyboardId}/scene/${idx}/preview-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            prompt: prompt || "",
            provider: resolvePreviewImageProvider(provider),
          }),
        }
      );
      if (!res.ok) {
        if (res.status === 404) {
          console.log("[VideoStoryboardPanel] Storyboard 404 on preview image");
          onStoryboardNotFound?.();
          setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
          return;
        }
        const errData = await res.json().catch(() => ({}));
        if (onInsufficientBalance?.(errData)) {
          setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      const jobId = data?.jobId;
      if (jobId) {
        // Async job → poll et. previewLoading poll boyunca açık kalır.
        pollPreviewJob(idx, jobId, 0);
        return;
      }
      // Fallback: sync response (eski davranış) hâlâ dönerse doğrudan kullan.
      const imageUrl = data?.scene?.previewImageUrl || data?.image?.url || data?.imageUrl || null;
      finishPreview(idx, imageUrl);
    } catch (err) {
      console.error("[VideoStoryboardPanel] Preview image error:", err);
      setPreviewLoading((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const hasAnyGenerating = Object.values(sceneJobs).some(
    (j) => j.status === "generating" || j.status === "processing"
  );

  const getSceneStatus = (scene) => {
    const job = sceneJobs[scene.index] || {};
    return job.status || scene.status || "pending";
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: i18n.t("FileModal.APIProviders.VideoGeneration.StatusPending"),
      generating: i18n.t("FileModal.APIProviders.VideoGeneration.StatusGenerating"),
      processing: i18n.t("FileModal.APIProviders.VideoGeneration.StatusProcessing"),
      completed: i18n.t("FileModal.APIProviders.VideoGeneration.StatusCompleted"),
      failed: i18n.t("FileModal.APIProviders.VideoGeneration.StatusFailed"),
      error: i18n.t("FileModal.APIProviders.VideoGeneration.StatusError"),
    };
    return statusMap[status] || status;
  };

  if (isCreating) {
    return (
      <div className="add-video-modal__storyboard">
        <div className="add-video-modal__status-card add-video-modal__status-card--processing">
          <div className="add-video-modal__status-title">
            <span className="add-video-modal__spinner" />
            {i18n.t("FileModal.APIProviders.VideoGeneration.StoryboardCreating")}
          </div>
        </div>
      </div>
    );
  }

  if (!scenes || scenes.length === 0) return null;

  return (
    <div className="add-video-modal__storyboard">
      <div
        className="add-video-modal__storyboard-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={onBack}
            className="add-video-modal__btn add-video-modal__btn--ghost"
            style={{
              padding: "6px 12px",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <LuArrowLeft size={14} />
            {i18n.t("FileModal.APIProviders.VideoGeneration.Back")}
          </button>
          <div>
            <span className="add-video-modal__storyboard-title">
              {i18n.t("FileModal.APIProviders.VideoGeneration.Storyboard")}
            </span>
            <span className="add-video-modal__storyboard-count" style={{ marginLeft: "8px" }}>
              {scenes.length} {i18n.t("FileModal.APIProviders.VideoGeneration.ScenesCount")}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="add-video-modal__btn add-video-modal__btn--ghost"
          style={{
            padding: "6px 12px",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            color: "#ef4444",
          }}
        >
          <LuTrash2 size={14} />
          {i18n.t("FileModal.APIProviders.VideoGeneration.Clear")}
        </button>
      </div>

      <div className="add-video-modal__storyboard-scenes">
        {scenes.map((scene) => {
          const idx = scene.index;
          const job = sceneJobs[idx] || {};
          const sceneStatus = job.status || scene.status || "pending";
          const sceneError = job.error || scene.error;
          const videoUrl = scene.videoUrl || job.result?.videoUrl;
          const isGenerating = sceneStatus === "generating" || sceneStatus === "processing";
          const isEditingThis = editingScene === idx;
          const canRegenerate =
            (sceneStatus === "completed" || sceneStatus === "failed" || sceneStatus === "error") &&
            !hasAnyGenerating;

          // Sıradaki sahne butonu mantığı
          const isPending = sceneStatus === "pending";
          const prevScene = idx > 0 ? scenes[idx - 1] : null;
          const prevSceneJob = prevScene ? sceneJobs[prevScene.index] || {} : null;
          const prevSceneStatus = prevSceneJob?.status || prevScene?.status || "pending";
          const canGenerateNext = isPending && (idx === 0 || prevSceneStatus === "completed");

          // Referans görsel yalnızca İLK sahnede (idx 0) — image-to-video zinciri
          // baştan başlar; sonraki sahneler önceki frame'i devraldığı için referans
          // koymak anlamsız ve karışıklık yaratır. Ayrıca üretilecek/yeniden
          // üretilecek durumda olmalı.
          const canSelectReference =
            idx === 0 &&
            ((isPending && canGenerateNext) ||
              sceneStatus === "completed" ||
              sceneStatus === "failed" ||
              sceneStatus === "error");

          return (
            <div
              key={idx}
              className="add-video-modal__scene-card"
              style={{ borderColor: STATUS_COLOR[sceneStatus] || "#e5e7eb" }}
            >
              {/* Header */}
              <div className="add-video-modal__scene-header">
                <div className="add-video-modal__scene-title">
                  <span className="add-video-modal__scene-num">
                    {i18n.t("FileModal.APIProviders.VideoGeneration.Scene")} {idx + 1}
                  </span>
                  <span className="add-video-modal__scene-time">
                    {scene.startTime}s – {scene.endTime}s
                  </span>
                </div>
                <span
                  className="add-video-modal__scene-badge"
                  style={{
                    background: STATUS_BG[sceneStatus] || "#f9fafb",
                    color: STATUS_COLOR[sceneStatus] || "#6b7280",
                  }}
                >
                  {isGenerating && <span className="add-video-modal__scene-spinner" />}
                  {getStatusLabel(sceneStatus)}
                </span>
              </div>

              {/* Progress bar while generating */}
              {isGenerating && (
                <div className="add-video-modal__progress" style={{ marginBottom: 12 }}>
                  <div
                    className="add-video-modal__progress-fill"
                    style={{ width: `${Math.max(5, job.progress || 0)}%` }}
                  />
                </div>
              )}

              {/* Error message */}
              {sceneError &&
                (sceneStatus === "error" || sceneStatus === "failed") &&
                !/cancel/i.test(String(sceneError)) && (
                  <div
                    className="add-video-modal__scene-error"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      padding: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#dc2626",
                        marginBottom: "4px",
                      }}
                    >
                      {i18n.t("FileModal.APIProviders.VideoGeneration.SceneError")}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        lineHeight: "1.5",
                        color: "#991b1b",
                      }}
                    >
                      {i18n.t("FileModal.APIProviders.VideoGeneration.ContentNotSuitable")}
                    </div>
                  </div>
                )}

              {/* Narration - More prominent */}
              <div
                className="add-video-modal__scene-narration"
                style={{
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "6px",
                  marginBottom: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  className="add-video-modal__scene-field-label"
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#334155",
                    marginBottom: "6px",
                  }}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Narration")}
                </div>
                <div
                  className="add-video-modal__scene-narration-text"
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#1e293b",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {scene.narrationSegmentDisplay || scene.narrationSegment}
                </div>
              </div>

              {/* Video Prompt - Collapsible */}
              <div className="add-video-modal__scene-prompt" style={{ marginBottom: "12px" }}>
                <div
                  className="add-video-modal__scene-field-label"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => togglePrompt(idx)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <LuChevronDown
                      size={14}
                      style={{
                        transform: expandedPrompts[idx] ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s",
                        color: "#64748b",
                      }}
                    />
                    <span style={{ fontSize: "12px", fontWeight: "500", color: "#64748b" }}>
                      {i18n.t("FileModal.APIProviders.VideoGeneration.VideoPrompt")}
                    </span>
                  </div>
                  {/* Edit her durumda (tamamlanmış dâhil) header'da görünür; prompt
                      akordeonu kapalı olsa da erişilebilir. Tıklayınca bölüm açılır,
                      düzenlenip Regenerate ile yeni prompt'la yeniden üretilebilir. */}
                  {!isEditingThis && !isGenerating && (
                    <button
                      type="button"
                      className="add-video-modal__scene-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditStart(idx, scene.videoPromptDisplay || scene.videoPrompt);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "11px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #cbd5e1",
                        background: "white",
                        color: "#475569",
                        cursor: "pointer",
                      }}
                    >
                      <LuPenLine size={11} />
                      {i18n.t("FileModal.APIProviders.VideoGeneration.Edit")}
                    </button>
                  )}
                </div>

                {expandedPrompts[idx] && (
                  <>
                    {isEditingThis ? (
                      <div className="add-video-modal__scene-edit-area">
                        <textarea
                          value={editPromptValue}
                          onChange={(e) => onEditChange(e.target.value)}
                          rows={4}
                          className="add-video-modal__scene-prompt-textarea"
                          style={inputStyle}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                        />
                        {editErrors[idx] && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              background: "#fef2f2",
                              border: "1px solid #fecaca",
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: "#991b1b",
                              lineHeight: "1.4",
                            }}
                          >
                            {editErrors[idx]}
                          </div>
                        )}
                        <div
                          className="add-video-modal__scene-edit-actions"
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "8px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            className="add-video-modal__btn add-video-modal__btn--primary"
                            style={{
                              fontSize: 12,
                              padding: "6px 14px",
                              borderRadius: "4px",
                              opacity: isSavingScene ? 0.7 : 1,
                            }}
                            onClick={() => handleEditSave(idx)}
                            disabled={isSavingScene}
                          >
                            {isSavingScene
                              ? i18n.t("FileModal.APIProviders.VideoGeneration.Saving") ||
                                "Kaydediliyor..."
                              : i18n.t("FileModal.APIProviders.VideoGeneration.Save")}
                          </button>
                          <button
                            type="button"
                            className="add-video-modal__btn add-video-modal__btn--ghost"
                            style={{ fontSize: 12, padding: "6px 14px", borderRadius: "4px" }}
                            onClick={handleEditCancel}
                          >
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Cancel")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="add-video-modal__scene-prompt-text"
                        style={{
                          fontSize: "13px",
                          lineHeight: "1.5",
                          color: "#475569",
                          padding: "8px",
                          background: "white",
                          borderRadius: "4px",
                          border: "1px solid #e2e8f0",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          maxHeight: "300px",
                          overflowY: "auto",
                        }}
                      >
                        {scene.videoPromptDisplay || scene.videoPrompt}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Completed: show video preview - Collapsible */}
              {sceneStatus === "completed" && videoUrl && (
                <div style={{ marginBottom: "12px" }}>
                  <div
                    className="add-video-modal__scene-field-label"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => toggleVideo(idx)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <LuChevronDown
                        size={14}
                        style={{
                          transform: expandedVideos[idx] ? "rotate(0deg)" : "rotate(-90deg)",
                          transition: "transform 0.2s",
                          color: "#64748b",
                        }}
                      />
                      <span style={{ fontSize: "12px", fontWeight: "500", color: "#64748b" }}>
                        {i18n.t("FileModal.APIProviders.VideoGeneration.VideoPreview")}
                      </span>
                    </div>
                  </div>

                  {expandedVideos[idx] && (
                    <div
                      style={{
                        width: aspectRatio === "9:16" ? "50%" : "calc(100% + 32px)",
                        marginLeft: aspectRatio === "9:16" ? "auto" : "-16px",
                        marginRight: aspectRatio === "9:16" ? "auto" : "-16px",
                        aspectRatio:
                          aspectRatio === "9:16"
                            ? "9/16"
                            : aspectRatio === "1:1"
                              ? "1/1"
                              : aspectRatio === "4:3"
                                ? "4/3"
                                : "16/9",
                        background: "#000",
                        overflow: "hidden",
                        borderRadius: aspectRatio === "9:16" ? "8px" : "0",
                      }}
                    >
                      <video
                        src={videoUrl}
                        controls
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Sahne Görselleri - Collapsible (referans olarak seç).
                  Sadece üretilecek/yeniden üretilecek sahnelerde gösterilir.
                  Önizleme görsellerinin ÜSTÜNDE (katman olarak önce) gösterilir. */}
              {sceneImages.length > 0 && canSelectReference && (
                <div style={{ marginBottom: "12px" }}>
                  <div
                    className="add-video-modal__scene-field-label"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => toggleSceneImages(idx)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <LuChevronDown
                        size={14}
                        style={{
                          transform: expandedSceneImages[idx] ? "rotate(0deg)" : "rotate(-90deg)",
                          transition: "transform 0.2s",
                          color: "#64748b",
                        }}
                      />
                      <span style={{ fontSize: "12px", fontWeight: "500", color: "#64748b" }}>
                        {i18n.t("FileModal.APIProviders.VideoGeneration.SceneImages")} (
                        {sceneImages.length})
                      </span>
                    </div>
                  </div>

                  {expandedSceneImages[idx] && (
                    <>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#94a3b8",
                          marginBottom: "8px",
                          lineHeight: "1.4",
                        }}
                      >
                        {i18n.t("FileModal.APIProviders.VideoGeneration.SceneImagesHint")}
                      </div>
                      <div style={selectableGridStyle}>
                        {sceneImages.map((img) =>
                          renderSelectableImageThumb(idx, img.url, img.boxId || img.url)
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Preview Image(s) - Collapsible, seçilebilir liste.
                  Sahne görsellerinin ALTINA (katman olarak sonra) eklenir. */}
              {(() => {
                const previewUrls = scene.previewImageUrls?.length
                  ? scene.previewImageUrls
                  : scene.previewImageUrl
                    ? [scene.previewImageUrl]
                    : [];
                if (!previewUrls.length) return null;
                return (
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      className="add-video-modal__scene-field-label"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => togglePreview(idx)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <LuChevronDown
                          size={14}
                          style={{
                            transform: expandedPreviews[idx] ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                            color: "#64748b",
                          }}
                        />
                        <span style={{ fontSize: "12px", fontWeight: "500", color: "#64748b" }}>
                          {i18n.t("FileModal.APIProviders.VideoGeneration.PreviewImage")}
                          {previewUrls.length > 1 ? ` (${previewUrls.length})` : ""}
                        </span>
                      </div>
                    </div>

                    {expandedPreviews[idx] && (
                      <>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#94a3b8",
                            marginBottom: "8px",
                            lineHeight: "1.4",
                          }}
                        >
                          {i18n.t("FileModal.APIProviders.VideoGeneration.SceneImagesHint")}
                        </div>
                        <div style={selectableGridStyle}>
                          {previewUrls.map((url) =>
                            renderSelectableImageThumb(
                              idx,
                              url,
                              `pv-${idx}-${url}`,
                              canSelectReference
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Cancel button while job is in progress */}
              {isGenerating && job.jobId && (
                <div
                  className="add-video-modal__scene-footer"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--ghost"
                    disabled={cancellingJobs[idx]}
                    onClick={() => handleCancelClick(idx)}
                    style={{
                      fontSize: 12,
                      padding: "4px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: "#ef4444",
                      borderColor: "#fecaca",
                      opacity: cancellingJobs[idx] ? 0.7 : 1,
                    }}
                  >
                    {cancellingJobs[idx] ? (
                      <span className="add-video-modal__scene-spinner" />
                    ) : (
                      <LuX size={12} />
                    )}
                    {cancellingJobs[idx]
                      ? i18n.t("FileModal.APIProviders.VideoGeneration.Cancelling") ||
                        "İptal ediliyor..."
                      : i18n.t("FileModal.APIProviders.VideoGeneration.Cancel") || "İptal Et"}
                  </button>
                </div>
              )}

              {/* Action buttons footer */}
              {(sceneStatus === "pending" ||
                sceneStatus === "completed" ||
                sceneStatus === "failed" ||
                sceneStatus === "error") && (
                <div
                  className="add-video-modal__scene-footer"
                  style={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  {/* Referans görsel (image-to-video): yalnızca ilk sahnede */}
                  {canSelectReference && renderRefImageControl(idx)}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {sceneStatus === "pending" && canGenerateNext && (
                        <button
                          type="button"
                          className="add-video-modal__btn add-video-modal__btn--primary"
                          onClick={() => onGenerateNext?.(refUrlToSend(idx))}
                          style={{
                            fontSize: 12,
                            padding: "4px 12px",
                          }}
                        >
                          {i18n.t("FileModal.APIProviders.VideoGeneration.NextScene")}
                        </button>
                      )}
                      <button
                        type="button"
                        className="add-video-modal__btn add-video-modal__btn--secondary"
                        disabled={previewLoading[idx]}
                        onClick={() =>
                          handlePreviewImage(idx, scene.videoPromptDisplay || scene.videoPrompt)
                        }
                        style={{
                          fontSize: 12,
                          padding: "4px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          opacity: previewLoading[idx] ? 0.7 : 1,
                        }}
                      >
                        {previewLoading[idx] ? (
                          <span className="add-video-modal__scene-spinner" />
                        ) : (
                          <LuImage size={12} />
                        )}
                        {previewLoading[idx]
                          ? i18n.t("FileModal.APIProviders.VideoGeneration.GeneratingPreview")
                          : scene.previewImageUrl || previewDone[idx]
                            ? i18n.t(
                                "FileModal.APIProviders.VideoGeneration.RegeneratePreviewImage"
                              )
                            : i18n.t("FileModal.APIProviders.VideoGeneration.GeneratePreviewImage")}
                      </button>
                      {sceneStatus === "completed" && videoUrl && (
                        <>
                          {addedFromTray(job.jobId || scene.jobId) && (
                            <span
                              style={{
                                fontSize: 12,
                                color: "#16a34a",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <LuCheck size={12} />
                              {i18n.t("FileModal.APIProviders.VideoGeneration.AddedFromTray")}
                            </span>
                          )}
                          <button
                            type="button"
                            className={`add-video-modal__btn ${
                              addedFromTray(job.jobId || scene.jobId)
                                ? "add-video-modal__btn--secondary"
                                : "add-video-modal__btn--primary"
                            }`}
                            onClick={() =>
                              onAddToScene?.(
                                videoUrl,
                                `${i18n.t("FileModal.APIProviders.VideoGeneration.Scene")} ${idx + 1}`,
                                job.jobId || scene.jobId
                              )
                            }
                            style={{
                              fontSize: 12,
                              padding: "4px 12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <LuPlus size={12} />
                            {addedFromTray(job.jobId || scene.jobId)
                              ? i18n.t("FileModal.APIProviders.VideoGeneration.AddAgain")
                              : i18n.t("FileModal.APIProviders.VideoGeneration.AddToScene")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload(videoUrl, `scene-${idx + 1}.mp4`)}
                            className="add-video-modal__btn add-video-modal__btn--secondary"
                            style={{
                              fontSize: 12,
                              padding: "4px 12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <LuDownload size={12} />
                            {i18n.t("FileModal.APIProviders.VideoGeneration.Download")}
                          </button>
                        </>
                      )}
                    </div>
                    {(sceneStatus === "completed" ||
                      sceneStatus === "failed" ||
                      sceneStatus === "error") && (
                      <button
                        type="button"
                        className="add-video-modal__btn add-video-modal__btn--ghost"
                        style={{ fontSize: 12, padding: "4px 12px" }}
                        disabled={!canRegenerate || isEditingThis}
                        onClick={() => onRegenerate(idx, refUrlToSend(idx))}
                      >
                        <LuRefreshCw size={12} style={{ marginRight: 4 }} />
                        {i18n.t("FileModal.APIProviders.VideoGeneration.Regenerate")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Yeni sahne ekleme */}
      {onAddScene && (
        <div
          className="add-video-modal__add-scene"
          style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px dashed #e2e8f0" }}
        >
          {!showAddForm ? (
            <button
              type="button"
              className="add-video-modal__btn add-video-modal__btn--secondary"
              disabled={hasAnyGenerating}
              onClick={() => setShowAddForm(true)}
              style={{
                width: "100%",
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                padding: "10px 12px",
                opacity: hasAnyGenerating ? 0.5 : 1,
                cursor: hasAnyGenerating ? "not-allowed" : "pointer",
              }}
            >
              <LuPlus size={14} />
              {i18n.t("FileModal.APIProviders.VideoGeneration.AddScene")}
            </button>
          ) : (
            <div className="add-video-modal__scene-card" style={{ borderColor: "#a963f5" }}>
              <div
                className="add-video-modal__scene-field-label"
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#334155",
                  marginBottom: "12px",
                }}
              >
                {i18n.t("FileModal.APIProviders.VideoGeneration.AddSceneTitle")}
              </div>

              {/* Video prompt (zorunlu, min 20). Narration bundan türetilir. */}
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#64748b",
                    marginBottom: "6px",
                  }}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.AddScenePromptLabel")}
                </div>
                <textarea
                  value={addPrompt}
                  onChange={(e) => setAddPrompt(e.target.value)}
                  rows={3}
                  placeholder={i18n.t(
                    "FileModal.APIProviders.VideoGeneration.AddScenePromptPlaceholder"
                  )}
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "6px" }}>
                  {i18n.t("FileModal.APIProviders.VideoGeneration.AddScenePromptHint")}
                </div>
              </div>

              {/* afterScene: konum seçimi ("" = sona ekle) */}
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#64748b",
                    marginBottom: "6px",
                  }}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.AddScenePositionLabel")}
                </div>
                <select
                  value={addAfterScene}
                  onChange={(e) => setAddAfterScene(e.target.value)}
                  style={{ ...inputStyle, cursor: "pointer" }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  <option value="">
                    {i18n.t("FileModal.APIProviders.VideoGeneration.AddScenePositionAppend")}
                  </option>
                  {/* afterScene 1-based sahne no. Son sahneden sonra = sona ekle olduğu
                      için son sahne hariç (1..N-1) listelenir. Ayrıca iki üretilmiş
                      (completed) videonun arasına yeni sahne eklenemez — image-to-video
                      zinciri (sahne önceki frame'i devralır) kırılacağı için o konum
                      seçeneği listelenmez. */}
                  {scenes.slice(0, -1).map((_, i) => {
                    const afterCompleted = getSceneStatus(scenes[i]) === "completed";
                    const nextCompleted = getSceneStatus(scenes[i + 1]) === "completed";
                    if (afterCompleted && nextCompleted) return null;
                    return (
                      <option key={i} value={i + 1}>
                        {i18n.t("FileModal.APIProviders.VideoGeneration.AddSceneAfterScene", {
                          n: i + 1,
                        })}
                      </option>
                    );
                  })}
                </select>
              </div>

              {addError && (
                <div
                  style={{
                    marginBottom: "12px",
                    padding: "8px 12px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "4px",
                    fontSize: "12px",
                    color: "#991b1b",
                    lineHeight: "1.4",
                  }}
                >
                  {addError}
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="add-video-modal__btn add-video-modal__btn--primary"
                  style={{
                    fontSize: 12,
                    padding: "6px 14px",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: isAddingScene || addPrompt.trim().length < 20 ? 0.7 : 1,
                  }}
                  disabled={isAddingScene || addPrompt.trim().length < 20}
                  onClick={handleAddSceneSubmit}
                >
                  {isAddingScene ? (
                    <span className="add-video-modal__scene-spinner" />
                  ) : (
                    <LuPlus size={12} />
                  )}
                  {isAddingScene
                    ? i18n.t("FileModal.APIProviders.VideoGeneration.AddSceneAdding")
                    : i18n.t("FileModal.APIProviders.VideoGeneration.AddScene")}
                </button>
                <button
                  type="button"
                  className="add-video-modal__btn add-video-modal__btn--ghost"
                  style={{ fontSize: 12, padding: "6px 14px", borderRadius: "4px" }}
                  disabled={isAddingScene}
                  onClick={resetAddForm}
                >
                  {i18n.t("FileModal.APIProviders.VideoGeneration.Cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fullscreen lightbox — portal ile body'ye; modal'ın transform'lu ata'sından
          kaçar, aksi halde position:fixed viewport yerine modal'a göre kayar. */}
      {lightboxUrl &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.88)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3000070, // above the dialog band — host VideoModal is a pageModal (3000000)
              cursor: "zoom-out",
            }}
          >
            <img
              src={lightboxUrl}
              alt="Fullscreen"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: 8,
                cursor: "default",
              }}
            />
            <button
              type="button"
              onClick={() => setLightboxUrl(null)}
              style={{
                position: "absolute",
                top: 20,
                right: 20,
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: "50%",
                width: 38,
                height: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                cursor: "pointer",
                backdropFilter: "blur(4px)",
              }}
            >
              <LuX size={18} />
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VideoStoryboardPanel;
